import { parseImageValidationTarget } from "./image-validation-target";
import { preservedImageURL } from "@glide/support";
import { ImageValidationDelegations } from "./image-validation-delegations";

export type ValidationSessionState = "off" | "starting" | "active";
export type ValidationTransport = (name: string, body: unknown) => Promise<unknown>;

/** One explicit app in one browser session. No global feature mutation or persistent storage. */
export class BrowserImageValidationSession {
    private revision = 0;
    private delegations = new ImageValidationDelegations();
    private token: string | undefined;
    private expiresAt = 0;
    private healthyUntil = 0;
    private timer: ReturnType<typeof setTimeout> | undefined;
    private currentState: ValidationSessionState = "off";

    constructor(
        private readonly appID: string,
        private readonly endpoint: string,
        private readonly transport: ValidationTransport,
        private readonly changed: (state: ValidationSessionState) => void,
        private readonly now: () => number = Date.now
    ) {}

    public stop(): void {
        const token = this.token;
        this.revision++;
        this.delegations.stop();
        this.delegations = new ImageValidationDelegations();
        this.token = undefined;
        this.expiresAt = this.healthyUntil = 0;
        if (this.timer !== undefined) clearTimeout(this.timer);
        this.timer = undefined;
        this.currentState = "off";
        this.changed("off");
        if (token !== undefined) this.revoke(token);
    }

    private revoke(token: string): void {
        // Local recovery never waits for the network. Server expiry bounds failed revocations.
        void this.call("revokeImageValidationSession", { appID: this.appID, token }).catch(() => undefined);
    }

    public get active(): boolean {
        return this.currentState === "active" && this.now() < Math.min(this.expiresAt, this.healthyUntil);
    }

    private async call(name: string, body: unknown): Promise<Record<string, unknown>> {
        let timeout: ReturnType<typeof setTimeout> | undefined;
        try {
            const value = await Promise.race([
                this.transport(name, body),
                new Promise<never>((_resolve, reject) => {
                    timeout = setTimeout(() => reject(new Error("Validation request timed out")), 8000);
                }),
            ]);
            if (typeof value !== "object" || value === null || Array.isArray(value))
                throw new Error("Invalid validation response");
            return value as Record<string, unknown>;
        } finally {
            if (timeout !== undefined) clearTimeout(timeout);
        }
    }

    private async check(revision: number): Promise<boolean> {
        const value = await this.call("imageValidationSessionStatus", { appID: this.appID, token: this.token });
        if (revision !== this.revision) return false;
        if (
            typeof value.revision !== "string" ||
            typeof value.expiresAt !== "number" ||
            !Number.isSafeInteger(value.expiresAt) ||
            value.expiresAt <= this.now()
        )
            throw new Error("Expired validation session");
        this.expiresAt = value.expiresAt;
        this.healthyUntil = this.now() + 10_000;
        return true;
    }

    private schedule(revision: number): void {
        this.timer = setTimeout(async () => {
            try {
                if (revision !== this.revision || !(await this.check(revision))) return;
                this.schedule(revision);
            } catch (e: unknown) {
                if (revision === this.revision) this.stop();
            }
        }, 5000);
    }

    public async start(): Promise<boolean> {
        this.stop();
        const revision = this.revision;
        this.currentState = "starting";
        this.changed("starting");
        try {
            const value = await this.call("createImageValidationSession", { appID: this.appID });
            if (revision !== this.revision) {
                if (typeof value.token === "string") this.revoke(value.token);
                return false;
            }
            if (typeof value.token !== "string" || !/^[A-Za-z0-9_-]{43}$/.test(value.token))
                throw new Error("Invalid session token");
            this.token = value.token;
            if (!(await this.check(revision))) return false;
            this.currentState = "active";
            this.changed("active");
            this.schedule(revision);
            return true;
        } catch (e: unknown) {
            if (revision === this.revision) this.stop();
            return false;
        }
    }

    /** Await before loading selected media; callers must observe stop to restore previously rendered URLs. */
    public async resolveMedia(appID: string, source: string, target?: string): Promise<string> {
        if (appID !== this.appID || !this.active) return source;
        const revision = this.revision;
        try {
            const destination = parseImageValidationTarget(
                target ?? preservedImageURL(source, this.endpoint),
                this.endpoint
            );
            if (destination === undefined || destination.token !== null) throw new Error("Invalid validation target");
            const resolved = await this.delegations.run(target ?? source, async () => {
                const value = await this.call("delegateImageValidationMedia", {
                    appID,
                    token: this.token,
                    ...(destination.sourceURL === undefined
                        ? { sourceKey: destination.sourceKey }
                        : { sourceURL: destination.sourceURL }),
                });
                if (revision !== this.revision || !this.active) return source;
                if (typeof value.token !== "string" || !/^[A-Za-z0-9_-]{43}$/.test(value.token))
                    throw new Error("Invalid media token");
                return destination.authorize(value.token);
            });
            return revision === this.revision && this.active ? resolved : source;
        } catch (e: unknown) {
            if (revision === this.revision) this.stop();
            return source;
        }
    }
}
